import ReactGA from "react-ga4";
import {
  ANALYTICS_STORAGE_KEYS,
  DEDUPE_WINDOW_MS,
  resolveEnvironment,
  resolveMeasurementId,
  isLocalDevelopment,
} from "./constants";

const safeNow = () => Date.now();

const createSessionId = () => {
  if (globalThis.crypto?.randomUUID) {
    return `s_${globalThis.crypto.randomUUID()}`;
  }

  if (globalThis.crypto?.getRandomValues) {
    const randomValues = globalThis.crypto.getRandomValues(new Uint32Array(2));
    return `s_${randomValues[0].toString(36)}${randomValues[1].toString(36)}`;
  }

  return `s_${safeNow()}`;
};

const getWeekNumber = (date) => {
  const tmp = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
};

const getQuarter = (month) => Math.floor(month / 3) + 1;

const getLanguage = () => globalThis?.navigator?.language || "en-US";

const getScreenResolution = () => {
  const width = globalThis?.screen?.width || 0;
  const height = globalThis?.screen?.height || 0;
  return `${width}x${height}`;
};

const getDeviceType = () => {
  const ua = (globalThis?.navigator?.userAgent || "").toLowerCase();
  if (/tablet|ipad/.test(ua)) return "tablet";
  if (/mobile|iphone|android/.test(ua)) return "mobile";
  return "desktop";
};

const getBrowser = () => {
  const ua = globalThis?.navigator?.userAgent || "";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Chrome")) return "Chrome";
  return "Unknown";
};

const getOperatingSystem = () => {
  const platform = globalThis?.navigator?.platform || "";
  const ua = globalThis?.navigator?.userAgent || "";

  if (/win/i.test(platform)) return "Windows";
  if (/mac/i.test(platform)) return "MacOS";
  if (/linux/i.test(platform)) return "Linux";
  if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
  if (/android/i.test(ua)) return "Android";
  return "Unknown";
};

const getTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const getOrCreateSessionId = () => {
  const stored = sessionStorage.getItem(ANALYTICS_STORAGE_KEYS.SESSION_ID);
  if (stored) return stored;

  const id = createSessionId();
  sessionStorage.setItem(ANALYTICS_STORAGE_KEYS.SESSION_ID, id);
  sessionStorage.setItem(
    ANALYTICS_STORAGE_KEYS.SESSION_START,
    String(safeNow()),
  );
  return id;
};

const getSessionDurationSeconds = () => {
  const start = Number(
    sessionStorage.getItem(ANALYTICS_STORAGE_KEYS.SESSION_START) || safeNow(),
  );
  return Math.max(0, Math.round((safeNow() - start) / 1000));
};

const stableStringify = (value) => {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;

  return `{${Object.keys(value)
    .sort((left, right) => left.localeCompare(right))
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

const compactObject = (value = {}) => {
  return Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => {
      return (
        entryValue !== null && entryValue !== undefined && entryValue !== ""
      );
    }),
  );
};

class AnalyticsService {
  initialized = false;

  enabled = false;

  useTagManager = false;

  queue = [];

  maxRetries = 2;

  retryBackoffMs = 300;

  context = {};

  lastEventKey = null;

  lastEventAt = 0;

  sessionId = getOrCreateSessionId();

  constructor() {
    globalThis.addEventListener("online", () => this.flushQueue());
  }

  init(config = {}) {
    if (this.initialized) return;

    const measurementId = config.measurementId || resolveMeasurementId();
    const environment = config.environment || resolveEnvironment();
    const gtmId = config.gtmId || process.env.REACT_APP_GTM_ID;
    const localDisable =
      config.disableOnLocal !== false ? isLocalDevelopment() : false;
    this.useTagManager =
      config.useTagManager === true ||
      String(process.env.REACT_APP_GA_USE_GTM || "").toLowerCase() === "true";

    const hasTagManager = this.useTagManager && Boolean(gtmId);
    const hasDirectAnalytics = !this.useTagManager && Boolean(measurementId);

    this.enabled = !localDisable && (hasTagManager || hasDirectAnalytics);
    this.context.environment = environment;
    this.context.application_version =
      process.env.REACT_APP_VERSION || "unknown";

    if (!this.enabled) {
      this.initialized = true;
      return;
    }

    if (hasTagManager) {
      this.injectGTM(gtmId);
    } else {
      ReactGA.initialize(measurementId, {
        gtagOptions: {
          send_page_view: false,
          anonymize_ip: true,
        },
      });

      ReactGA.set({
        app_version: this.context.application_version,
        environment: environment,
      });
    }

    this.initialized = true;
    this.flushQueue();
  }

  injectGTM(gtmId) {
    if (document.getElementById("analytics-gtm-script")) return;

    const script = document.createElement("script");
    script.id = "analytics-gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);

    globalThis.dataLayer = globalThis.dataLayer || [];
    globalThis.dataLayer.push({
      event: "gtm.js",
      "gtm.start": safeNow(),
    });
  }

  setUser(userId, properties = {}) {
    if (!userId) return;

    this.context.user_id = userId;
    const safeProperties = compactObject(properties);

    if (this.enabled) {
      if (this.useTagManager) {
        globalThis.dataLayer = globalThis.dataLayer || [];
        globalThis.dataLayer.push({
          user_id: userId,
          user_properties: safeProperties,
        });
      } else {
        ReactGA.set({ user_id: userId });
        if (typeof ReactGA.gtag === "function") {
          ReactGA.gtag("set", "user_properties", safeProperties);
        }
      }
    }
  }

  clearUser() {
    delete this.context.user_id;

    if (!this.enabled) return;

    if (this.useTagManager) {
      globalThis.dataLayer = globalThis.dataLayer || [];
      globalThis.dataLayer.push({
        user_id: undefined,
        user_properties: undefined,
      });
      return;
    }

    ReactGA.set({ user_id: undefined });
  }

  setContext(partialContext = {}) {
    this.context = {
      ...this.context,
      ...partialContext,
    };
  }

  setLoginTime(value) {
    if (!value) return;
    sessionStorage.setItem(ANALYTICS_STORAGE_KEYS.LOGIN_TIME, String(value));
  }

  getDefaultEventParams() {
    const now = new Date();

    return {
      environment: this.context.environment || resolveEnvironment(),
      app_version:
        this.context.application_version ||
        process.env.REACT_APP_VERSION ||
        "unknown",
      timestamp: now.toISOString(),
      hour: now.getHours(),
      day: now.getDate(),
      week: getWeekNumber(now),
      month: now.getMonth() + 1,
      quarter: getQuarter(now.getMonth()),
      year: now.getFullYear(),
      timezone: getTimezone(),
      analytics_session_id: this.sessionId,
      session_duration: getSessionDurationSeconds(),
      login_time:
        sessionStorage.getItem(ANALYTICS_STORAGE_KEYS.LOGIN_TIME) || null,
      page_location: globalThis?.location?.href,
      page_path: globalThis?.location?.pathname,
      page_title: document?.title,
      referrer: document?.referrer || null,
      screen_resolution: getScreenResolution(),
      language: getLanguage(),
      browser: getBrowser(),
      operating_system: getOperatingSystem(),
      device_type: getDeviceType(),
    };
  }

  buildEventPayload(params = {}) {
    return {
      ...this.getDefaultEventParams(),
      ...this.context,
      ...params,
    };
  }

  shouldDedupe(eventName, payload) {
    const eventKey = `${eventName}:${stableStringify(payload)}`;
    const now = safeNow();

    if (
      this.lastEventKey === eventKey &&
      now - this.lastEventAt <= DEDUPE_WINDOW_MS
    ) {
      return true;
    }

    this.lastEventKey = eventKey;
    this.lastEventAt = now;
    return false;
  }

  track(eventName, params = {}, options = {}) {
    const payload = this.buildEventPayload(params);

    if (options.dedupe !== false && this.shouldDedupe(eventName, payload)) {
      return;
    }

    if (!this.enabled) {
      return;
    }

    if (!this.initialized || !globalThis.navigator.onLine) {
      this.queue.push({ eventName, params, retries: 0 });
      return;
    }

    this.dispatchEvent({ eventName, params, retries: 0 });
  }

  dispatchEvent(item) {
    try {
      const payload = this.buildEventPayload(item.params);

      if (this.useTagManager) {
        globalThis.dataLayer = globalThis.dataLayer || [];
        globalThis.dataLayer.push({
          event: item.eventName,
          ...compactObject(payload),
        });
        return;
      }

      ReactGA.event(item.eventName, compactObject(payload));
    } catch (error) {
      if (item.retries < this.maxRetries) {
        const retries = item.retries + 1;
        const delay = this.retryBackoffMs * Math.pow(2, retries);
        setTimeout(() => {
          this.dispatchEvent({ ...item, retries });
        }, delay);
        return;
      }

      console.warn("Analytics dispatch failed", error);
    }
  }

  flushQueue() {
    if (!this.initialized || !this.enabled || !globalThis.navigator.onLine)
      return;

    const pending = [...this.queue];
    this.queue = [];

    pending.forEach((item) => this.dispatchEvent(item));
  }

  trackPageView(params = {}) {
    this.track("page_view", params);
  }

  trackError(params = {}) {
    this.track("application_error", params, { dedupe: false });
  }

  trackPerformance(params = {}) {
    this.track("performance_metric", params);
  }

  trackAPI(params = {}) {
    this.track("api_response", params, { dedupe: false });
  }

  getSessionId() {
    return this.sessionId;
  }
}

const analyticsService = new AnalyticsService();

export default analyticsService;
